import { useMemo } from 'react';
import { buildEntries, normalizeOrder, moveInOrder, moveRelative, geojsonBounds } from '../lib/layer-tree';
import { exportGeoJSON, exportFeaturesAsCSV, exportFeatureCollectionToKML } from '../lib/export';
import { useDrawOrderSync } from './useDrawOrderSync';

const safeName = (s) => String(s || 'layer').replace(/[^\w.-]+/g, '_').slice(0, 60);

/** Entry key -> the object the owning hook holds for it (or undefined). */
function mapEntryActions(entry, { lyr, io, raster, vt, pds }) {
  const { kind, id } = entry;
  const patch = (setter, fn) => setter((ls) => ls.map((x) => (x.id === id ? fn(x) : x)));
  switch (kind) {
    case 'project': return {
      toggle: () => lyr.toggleLayer(id),
      setOpacity: (v) => lyr.setOpacity(id, v),
    };
    case 'imported': return {
      toggle: () => patch(lyr.setImportedLayers, (x) => ({ ...x, visible: !x.visible })),
      setOpacity: (v) => lyr.setLayerOpacity(id, v),
      rename: (name) => patch(lyr.setImportedLayers, (x) => ({ ...x, name })),
      remove: () => lyr.setImportedLayers((ls) => ls.filter((x) => x.id !== id)),
      duplicate: () => lyr.setImportedLayers((ls) => {
        const src = ls.find((x) => x.id === id);
        return src ? [...ls, { ...src, id: `import-${Date.now()}-copy`, name: `${src.name} (copy)` }] : ls;
      }),
    };
    case 'result': return {
      toggle: () => patch(lyr.setResultLayers, (x) => ({ ...x, visible: !x.visible })),
      setOpacity: (v) => lyr.setLayerOpacity(id, v),
      rename: (name) => patch(lyr.setResultLayers, (x) => ({ ...x, name })),
      remove: () => lyr.setResultLayers((ls) => ls.filter((x) => x.id !== id)),
      duplicate: () => lyr.setResultLayers((ls) => {
        const src = ls.find((x) => x.id === id);
        return src ? [...ls, { ...src, id: `result-${Date.now()}-copy`, name: `${src.name} (copy)` }] : ls;
      }),
    };
    case 'raster': return {
      toggle: () => raster?.toggleLayer(id),
      setOpacity: (v) => raster?.setOpacity(id, v / 100),
      remove: () => raster?.removeLayer(id),
      zoomTo: () => raster?.zoomTo(id),
    };
    case 'project-dataset': return { toggle: () => pds?.load(id) };
    case 'vector-tile': return { toggle: () => vt?.toggleLayer(id) };
    case 'service': return {
      toggle: () => patch(io.setServiceLayers, (x) => ({ ...x, visible: !x.visible })),
      remove: () => io.setServiceLayers((ls) => ls.filter((x) => x.id !== id)),
    };
    default: return {};
  }
}

/**
 * The layer tree's data and handlers: entries (lib/layer-tree.js), the normalised draw order,
 * the MapLibre order sync, and one `actions` object the panel calls. `onOpenProperties(layerId)`
 * is optional: without it Properties opens the Style editor on that layer.
 */
export function useLayerTree({ lyr, io, raster, vt, pds, ui, usgsData, mapRefHolder, onOpenProperties, onRemoveDataset }) {
  const entries = useMemo(() => buildEntries({
    layers: lyr.layers, usgsData, importedLayers: lyr.importedLayers, resultLayers: lyr.resultLayers,
    rasterLayers: raster?.rasterLayers || [], vtLayers: vt?.layers || [], serviceLayers: io.serviceLayers,
    layerStyles: lyr.layerStyles, layerOpacity: lyr.layerOpacity,
    projectDatasets: pds?.datasets, projectDatasetStatus: pds?.status,
  }), [pds?.datasets, pds?.status, lyr.layers, usgsData, lyr.importedLayers, lyr.resultLayers, raster?.rasterLayers, vt?.layers, io.serviceLayers, lyr.layerStyles, lyr.layerOpacity]);

  const order = useMemo(() => normalizeOrder(lyr.layerOrder, entries.map((e) => e.key)), [lyr.layerOrder, entries]);

  useDrawOrderSync({ mapRefHolder, entries, order });

  const byKey = useMemo(() => new Map(entries.map((e) => [e.key, e])), [entries]);
  const ctx = { lyr, io, raster, vt, pds };
  const withEntry = (key, fn) => { const e = byKey.get(key); return e ? fn(e) : undefined; };
  const fitBounds = (bounds) => {
    const map = mapRefHolder.current?.current?.getMap?.();
    if (map && bounds) {
      if (bounds[0][0] === bounds[1][0] && bounds[0][1] === bounds[1][1]) map.jumpTo({ center: bounds[0], zoom: 16 });
      else map.fitBounds(bounds, { padding: 60, duration: 600, maxZoom: 18 });
    }
  };

  const actions = {
    toggle: (key) => withEntry(key, (e) => mapEntryActions(e, ctx).toggle?.()),
    setOpacity: (key, v) => withEntry(key, (e) => mapEntryActions(e, ctx).setOpacity?.(v)),
    rename: (key, name) => withEntry(key, (e) => { if (name && name.trim()) mapEntryActions(e, ctx).rename?.(name.trim()); }),
    remove: (key) => withEntry(key, (e) => {
      const dsId = e.kind === 'result' ? lyr.resultLayers.find((x) => x.id === e.id)?.dataset?.id
        : e.kind === 'imported' ? lyr.importedLayers.find((x) => x.id === e.id)?.projectDatasetId
          : e.kind === 'raster' ? raster?.rasterLayers.find((x) => x.id === e.id)?.datasetId : null;
      mapEntryActions(e, ctx).remove?.();
      if (dsId && onRemoveDataset) onRemoveDataset(dsId);
      if (lyr.activeLayerKey === key) lyr.setActiveLayerKey(null);
    }),
    duplicate: (key) => withEntry(key, (e) => mapEntryActions(e, ctx).duplicate?.()),
    zoomTo: (key) => withEntry(key, (e) => {
      const own = mapEntryActions(e, ctx).zoomTo;
      if (own) own();
      else fitBounds(e.bounds || geojsonBounds(e.geojson));
    }),
    exportAs: (key, format) => withEntry(key, (e) => {
      const fc = e.geojson;
      if (!fc?.features?.length) return;
      const base = safeName(e.name);
      if (format === 'geojson') exportGeoJSON(fc, `${base}.geojson`);
      else if (format === 'csv') exportFeaturesAsCSV(fc.features, `${base}.csv`);
      else if (format === 'kml') exportFeatureCollectionToKML(fc, e.name, `${base}.kml`);
    }),
    openProperties: (key) => withEntry(key, (e) => {
      if (!e.styleId) return;
      if (onOpenProperties) onOpenProperties(e.styleId);
      else { lyr.setStyleTargetLayer(e.styleId); ui.setShowStyleEditor(true); }
    }),
    openTable: (key) => withEntry(key, () => { lyr.setActiveLayerKey(key); ui.setShowAttributeTable(true); }),
    setActive: (key) => lyr.setActiveLayerKey(key),
    move: (key, op) => lyr.setLayerOrder(moveInOrder(order, key, op)),
    moveRelative: (key, targetKey, position) => lyr.setLayerOrder(moveRelative(order, key, targetKey, position)),
    // Several layers at once (sub-group rows): fit to the union of their extents.
    zoomToKeys: (keys) => {
      let b = null;
      keys.forEach((k) => {
        const e = byKey.get(k);
        const eb = e && (e.bounds || geojsonBounds(e.geojson));
        if (!eb) return;
        b = b ? [[Math.min(b[0][0], eb[0][0]), Math.min(b[0][1], eb[0][1])], [Math.max(b[1][0], eb[1][0]), Math.max(b[1][1], eb[1][1])]] : eb;
      });
      fitBounds(b);
    },
    // Viewing layers saved as project datasets: drop them from Viewing, add the stored datasets to the map.
    layersSaved: (saved) => {
      const ids = new Set(saved.map((s) => s.layerId));
      lyr.setResultLayers((ls) => ls.filter((x) => !ids.has(x.id)));
      saved.forEach((s) => pds?.addDataset?.(s.dataset, s.geojson));
    },
    importFile: () => io.setShowFileImport(true),
    setVtZField: (key, field) => withEntry(key, (e) => vt?.setZField(e.id, field)),
  };

  return { entries, order, actions };
}
